/**
 * DI-3-S68 B2 (DI-D70 c5) — a set that stopped on a TRANSPORT failure of its
 * create, its row batches or its `metadata-complete` is retried by itself:
 *
 *   · on the window's `online` event, and
 *   · on a backoff ladder 10 s, 30 s, 1 min, 2 min, 5 min — then it stops
 *     and waits for the pilot's Retry.
 *
 * The guards (each unit-tested, `intakeRunner.autoRetry.test.ts`):
 *
 *   single-flight   a set in `creating` / `syncing` / `completing` is never
 *                   re-entered; the runner's queue still sends one request
 *                   at a time;
 *   one tab         only the tab that holds the runner claim (`claim.ts`)
 *                   retries (the runner's gate, wired by `resume.ts`);
 *   no 4xx          a refusal the server ANSWERED is never retried by
 *                   itself — only "no answer" (network, timeout) and the
 *                   gateway's own 502 / 503 / 504;
 *   persisted       the attempt counter and the next attempt's time live in
 *                   the session record (`ClusterIntake.autoRetry`), so a
 *                   reload neither restarts nor duplicates the ladder;
 *   by hand         Retry resets the ladder.
 *
 * Replays are safe on the server: the create key is uuidv5(session:cluster),
 * a point's id uuidv5(da:payloadSha256), `metadata-complete` idempotent.
 */
import type { SetError, SetPhase } from "./phases.js";

/** The waits before attempt 1…5; after the fifth the set waits for Retry. */
export const RETRY_LADDER_MS: readonly number[] = [
  10_000, 30_000, 60_000, 120_000, 300_000,
];

/** The ladder's state for one set (persisted with the cluster). */
export interface AutoRetry {
  /** Automatic attempts made so far (0 … RETRY_LADDER_MS.length). */
  attempts: number;
  /** When the next automatic attempt is due; null = none (in flight, or the ladder ended). */
  nextAt: string | null;
}

/** The phases a step is in flight in — never re-entered by a retry. */
export const IN_FLIGHT: readonly SetPhase[] = [
  "creating",
  "syncing",
  "completing",
];

/** The gateway itself could not reach the service — no answer from it. */
const GATEWAY_TRANSPORT: ReadonlySet<number> = new Set([502, 503, 504]);

/**
 * A transport failure: no answer at all (network, `RequestTimeoutError`),
 * or the gateway's 502 / 503 / 504. Anything carrying a reason of the
 * browser's own (unreadable files, a folder without the set) is not one.
 */
export function isTransportFailure(e: SetError): boolean {
  if (e.heldAcquisitionId) return false;
  if (e.status === null) return e.reason === null;
  return GATEWAY_TRANSPORT.has(e.status);
}

/**
 * The ladder after a failed step: null when the failure is not transport
 * (it waits for Retry); else the next rung — `nextAt` null once all five
 * automatic attempts were made.
 */
export function ladderAfterFailure(
  prev: AutoRetry | null | undefined,
  error: SetError,
  now: Date,
): AutoRetry | null {
  if (!isTransportFailure(error)) return null;
  const attempts = prev?.attempts ?? 0;
  const wait = RETRY_LADDER_MS[attempts];
  return {
    attempts,
    nextAt:
      wait === undefined ? null : new Date(now.getTime() + wait).toISOString(),
  };
}

/** The ladder as an attempt starts: one more made, none due while it runs. */
export function ladderOnAttempt(prev: AutoRetry): AutoRetry {
  return { attempts: prev.attempts + 1, nextAt: null };
}

/** May an automatic attempt start for this set now? (`online` ignores the clock.) */
export function retryDue(
  s: {
    phase: SetPhase;
    error: SetError | null;
    autoRetry?: AutoRetry | null | undefined;
  },
  now: Date,
  trigger: "timer" | "online",
): boolean {
  if (!s.error || IN_FLIGHT.includes(s.phase)) return false;
  const at = s.autoRetry?.nextAt;
  if (!at) return false;
  if (trigger === "online") return true;
  return Date.parse(at) <= now.getTime();
}

/** "retrying by itself in 30 s" / "… at 14:05" — the error line's tail; null when none is due. */
export function autoRetryLine(
  a: AutoRetry | null | undefined,
  now: Date,
): string | null {
  if (!a?.nextAt) return null;
  const ms = Date.parse(a.nextAt) - now.getTime();
  if (!Number.isFinite(ms)) return null;
  if (ms <= 60_000)
    return `Retrying by itself in ${String(Math.max(1, Math.round(ms / 1000)))} s.`;
  const at = new Date(a.nextAt);
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `Retrying by itself at ${pad(at.getHours())}:${pad(at.getMinutes())}.`;
}
